import { Button, Group, Stack, Text, Select } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useState, useCallback, useMemo, useEffect } from "react";
import { useMedplum } from "@medplum/react";

import { useBenchmarkStaffSearch } from "@/hooks/shared/useFHIROptions";
import {
  reassignTask,
  reassignServiceApprovalNotificationForTask,
  APPROVE_SELECTED_SERVICES_CODE,
} from "@benchmark/core/actions";
import { getTaskAssigneeName } from "@benchmark/core/resolvers";
import { toLogSafeError } from "@benchmark/core/utils";
import {
  showErrorNotification,
  showSuccessNotification,
} from "@benchmark/ui/utils";
import { CONFIG } from "@/app/config";

import type { Task } from "@medplum/fhirtypes";

/**
 * Props for the ReassignTaskForm component.
 * @agentic connections
 */
type ReassignTaskFormProps = {
  task: Task;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/**
 * Modal form for reassigning a task to a different practitioner.
 * @agentic connections
 */
export const ReassignTaskForm = ({
  task,
  onSuccess,
  onCancel,
}: ReassignTaskFormProps) => {
  const medplum = useMedplum();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPractitionerRoleId, setSelectedPractitionerRoleId] = useState<
    string | null
  >(null);
  const [selectedPractitionerRoleLabel, setSelectedPractitionerRoleLabel] =
    useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const benchmarkOrganizationId = CONFIG.VITE_BENCHMARK_ORGANIZATION_ID;

  const { options: practitionerRoleOptions, isSearching } =
    useBenchmarkStaffSearch(searchQuery, benchmarkOrganizationId, {
      enabled: Boolean(benchmarkOrganizationId),
    });

  const resolvedOptions = useMemo(() => {
    if (searchQuery.trim().length >= 2) {
      return practitionerRoleOptions;
    }
    if (selectedPractitionerRoleId && selectedPractitionerRoleLabel) {
      return [
        {
          value: selectedPractitionerRoleId,
          label: selectedPractitionerRoleLabel,
        },
      ];
    }
    return [];
  }, [
    practitionerRoleOptions,
    searchQuery,
    selectedPractitionerRoleId,
    selectedPractitionerRoleLabel,
  ]);

  const derivedSelectedOption = useMemo(
    () =>
      practitionerRoleOptions.find(
        (option) => option.value === selectedPractitionerRoleId,
      ) ?? null,
    [practitionerRoleOptions, selectedPractitionerRoleId],
  );

  useEffect(() => {
    queueMicrotask(() => {
      if (!selectedPractitionerRoleId) {
        setSelectedPractitionerRoleLabel(null);
        return;
      }
      if (derivedSelectedOption) {
        setSelectedPractitionerRoleLabel(derivedSelectedOption.label);
      }
    });
  }, [derivedSelectedOption, selectedPractitionerRoleId]);

  const nothingFoundMessage =
    searchQuery.trim().length < 2
      ? "Type at least 2 characters to search"
      : isSearching
        ? "Searching..."
        : "No practitioners found";

  const currentAssignee = getTaskAssigneeName(task);

  const handleSubmit = useCallback(async () => {
    if (!selectedPractitionerRoleId || !selectedPractitionerRoleLabel) return;
    if (!task.id) {
      showErrorNotification("Cannot reassign task: missing task ID.");
      return;
    }

    setIsSubmitting(true);
    try {
      await reassignTask(medplum, {
        taskId: task.id,
        newOwnerRef: selectedPractitionerRoleId,
        newOwnerDisplay: selectedPractitionerRoleLabel,
      });
      // Move any service-approval notice to the new owner (B7/D9). Best-effort;
      // only relevant for approval tasks, and a no-op when no notice exists.
      const isApprovalTask = (task.code?.coding ?? []).some(
        (coding) => coding.code === APPROVE_SELECTED_SERVICES_CODE,
      );
      if (isApprovalTask) {
        try {
          await reassignServiceApprovalNotificationForTask(medplum, {
            approvalTaskIdOrRef: task.id,
            newOwnerRef: selectedPractitionerRoleId,
            newOwnerDisplay: selectedPractitionerRoleLabel,
          });
        } catch (error) {
          console.error(
            "Failed to move service-approval notification on reassignment:",
            toLogSafeError(error),
          );
        }
      }
      showSuccessNotification("Task reassigned successfully");
      onSuccess?.();
    } catch (error) {
      console.error("Error reassigning task:", error);
      showErrorNotification("Failed to reassign task. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    medplum,
    task,
    selectedPractitionerRoleId,
    selectedPractitionerRoleLabel,
    onSuccess,
  ]);

  return (
    <Stack gap="md">
      {currentAssignee && (
        <Text size="sm" c="dimmed" ta="center" mt="sm">
          Currently assigned to: <strong>{currentAssignee}</strong>
        </Text>
      )}

      <Select
        label="Reassign to"
        data={resolvedOptions}
        searchable
        clearable={false}
        size="sm"
        value={selectedPractitionerRoleId}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        onChange={setSelectedPractitionerRoleId}
        filter={({ options: selectOptions }) => selectOptions}
        nothingFoundMessage={nothingFoundMessage}
        placeholder="Search practitioner"
      />

      <Group justify="flex-end" gap="sm" mt="sm">
        <Button
          variant="outline"
          onClick={onCancel}
          leftSection={<IconX size={16} />}
        >
          Cancel
        </Button>
        <Button
          color="purple"
          onClick={() => {
            void handleSubmit();
          }}
          loading={isSubmitting}
          disabled={!selectedPractitionerRoleId || isSubmitting}
          leftSection={<IconCheck size={16} />}
        >
          Reassign
        </Button>
      </Group>
    </Stack>
  );
};
